import type { DeepLink, DeltaBase, MetricValue, UserRef } from '@ops/contracts'
import type { ReactNode } from 'react'

import { formatMetricValue, iniciales } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import {
  Button,
  Card,
  DeltaTag,
  ExternalLink,
  HealthDot,
  KpiValor,
  ProvenanceBadge,
  RecordChip,
  SEVERIDAD_LABEL,
  Stack,
  StatusPill,
} from '../../ui'
import type { Cobertura, Frente } from './frentes'

/**
 * Un frente de la Zona 1: qué está roto, **de quién es** y qué se puede hacer
 * hoy. La severidad viaja en la pill (texto + icono) además del borde izquierdo
 * de la card: el color nunca es la única señal.
 *
 * El rótulo de cobertura es el injerto 5 del briefing: cuando el frente ya tiene
 * un seguimiento en Plane se ve "Ya tiene seguimiento: OPS-142 · En curso" y
 * cuando no, el botón de crearlo. De un golpe se ve qué frentes están huérfanos.
 */
export interface FrenteCardProps {
  frente: Frente
  /** Dueño del frente. `null` se dice en palabras, no se esconde. */
  owner: UserRef | null
  /** El seguimiento que ya cubre este frente, si existe. */
  cobertura?: Cobertura
  /** Abre el diálogo de crear seguimiento en Plane. */
  onCrearSeguimiento: (frente: Frente) => void
  /** Deep link al registro del sistema de origen, cuando la entidad tiene uno. */
  linkOrigen?: DeepLink | null
  /** Qué sistema falta vincular cuando `linkOrigen` es null. */
  sistemaFaltante?: string
  /** Acción extra propia del tipo de frente, ej. "Cargalo acá". */
  acciones?: ReactNode
  /**
   * La métrica del frente, cuando el frente es una métrica del tablero. Sin
   * esto el frente diría "Margen bruto fuera de umbral" **sin mostrar el
   * número**, y un frente que no publica su cifra no se puede discutir.
   */
  metric?: MetricValue | null
  /** Base del delta de la métrica. La regla de derivables la exige. */
  base?: DeltaBase | null
  direccion?: 'higher_better' | 'lower_better'
  /** Reloj compartido de la pantalla, para la frescura del badge. */
  now?: Date
  /** Abre el drawer de detalle de la métrica. */
  onVerDetalle?: (metric: MetricValue) => void
}

export function FrenteCard({
  frente,
  owner,
  cobertura,
  onCrearSeguimiento,
  linkOrigen,
  sistemaFaltante,
  acciones,
  metric,
  base,
  direccion,
  now,
  onVerDetalle,
}: FrenteCardProps) {
  const vincular = useVincular()
  const conNumero = metric !== null && metric !== undefined && metric.valor !== null

  return (
    <Card
      as="li"
      severidad={frente.severidad}
      padding="md"
      className="list-none"
    >
      <Stack gap="sm" align="start">
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <StatusPill severidad={frente.severidad} caps size="sm">
            {SEVERIDAD_LABEL[frente.severidad]}
          </StatusPill>
          <span className="inline-flex items-center gap-1.5 text-micro text-muted-strong">
            <span
              aria-hidden="true"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-n100 text-[9px] font-bold text-text"
            >
              {iniciales(owner?.nombre)}
            </span>
            {owner === null ? (
              <span data-testid="frente-sin-dueno">Sin dueño asignado</span>
            ) : (
              <>
                <span className="sr-only">Dueño: </span>
                {owner.nombre}
              </>
            )}
          </span>
        </div>

        <h4
          data-testid="frente-titulo"
          className="text-titulo font-medium text-text"
        >
          {frente.titulo}
        </h4>
        <p className="max-w-prosa text-meta text-muted-strong">
          {frente.detalle}
        </p>

        {conNumero &&
        metric !== null &&
        metric !== undefined &&
        metric.valor !== null ? (
          <div
            data-testid="frente-numero"
            className="flex w-full flex-wrap items-end gap-x-4 gap-y-1"
          >
            <KpiValor valor={metric.valor} unidad={metric.unidad} />
            <span className="flex flex-col gap-1">
              <HealthDot health={metric.health} size="sm" />
              <DeltaTag
                deltaPct={metric.deltaPct}
                base={base}
                unidad={metric.unidad}
                direccion={direccion}
                size="sm"
              />
              {/* Regla 1: ninguna cifra del panel se muestra sin su fuente. */}
              <ProvenanceBadge
                provenance={
                  metric.provenance.updatedAt === null ? null : metric.provenance
                }
                now={now}
                hrefAdmin="/admin"
                variant="inline"
              />
            </span>
            {metric.benchmark !== null ? (
              <span className="text-micro text-muted-strong">
                Referencia: {metric.benchmark}
              </span>
            ) : null}
          </div>
        ) : null}

        {linkOrigen !== undefined ? (
          <ExternalLink
            link={linkOrigen}
            variante="chip"
            sistemaFaltante={sistemaFaltante}
            onVincular={() =>
              vincular(
                sistemaFaltante === undefined
                  ? frente.titulo
                  : `${frente.titulo} en ${sistemaFaltante}`,
              )
            }
          />
        ) : null}

        <div className="flex w-full flex-wrap items-center gap-2">
          {cobertura === undefined ? (
            <RecordChip
              link={null}
              rotuloVacio="Sin seguimiento"
              onCrear={() => onCrearSeguimiento(frente)}
              textoCrear="Asignar seguimiento"
            />
          ) : (
            <RecordChip
              link={cobertura.link}
              identificador={cobertura.followup.planeIdentificador}
              estado={cobertura.followup.estado}
              estadoGroup={cobertura.followup.estadoGroup}
              rotulo="Ya tiene seguimiento:"
            />
          )}
          {onVerDetalle !== undefined && metric !== null && metric !== undefined ? (
            <Button variante="chico" onClick={() => onVerDetalle(metric)}>
              Ver detalle
              <span className="sr-only"> de {metric.nombre}</span>
            </Button>
          ) : null}
          {acciones}
        </div>
      </Stack>
    </Card>
  )
}

export interface FrentesListaProps {
  frentes: readonly Frente[]
  children: (frente: Frente) => ReactNode
  /** Qué decir cuando no hay ninguno. Nunca una lista vacía sin explicación. */
  vacio: ReactNode
}

/** La lista de frentes como `ul` real, con su estado vacío. */
export function FrentesLista({ frentes, children, vacio }: FrentesListaProps) {
  if (frentes.length === 0) return <>{vacio}</>

  return (
    <Stack as="ul" gap="sm">
      {frentes.map((frente) => children(frente))}
    </Stack>
  )
}
